/* eslint-disable no-bitwise, no-param-reassign, @typescript-eslint/no-magic-numbers */

import { isNil } from '~/utilities/type-guards';

export type ColorTuple = [number, number, number];

const mapStringToNumberTuple = (arr: string[], fn: (value: string) => number): ColorTuple => {
  if (arr.length !== 3) {
    throw new Error('Array must have exactly 3 elements to be converted to a ColorTuple');
  }

  const mappedArray = arr.map(fn);
  return mappedArray as ColorTuple;
};

const cappedRoundRgbVal = (val: number) => Math.max(0, Math.min(255, Math.round(val)));

export const hexValueFromRgb = (red: number, green: number, blue: number): string => {
  let hexVal = (
    (cappedRoundRgbVal(red) << 16) |
    (cappedRoundRgbVal(green) << 8) |
    (cappedRoundRgbVal(blue) << 0)
  ).toString(16);

  const zeroesToAdd = 6 - hexVal.length;
  const zeroes: string[] = [];
  for (let i = 0; i < zeroesToAdd; i += 1) {
    zeroes.push('0');
  }
  hexVal = `${zeroes.join('')}${hexVal}`;
  return hexVal;
};

const ONE_SIXTH = 1 / 6;
const ONE_THIRD = 1 / 3;
const HALF = 1 / 2;
const TWO_THIRDS = 2 / 3;
const hue2rgb = (minRgb: number, chroma: number, hue: number) => {
  if (hue < 0) {
    hue += 1;
  }
  if (hue > 1) {
    hue -= 1;
  }
  if (hue < ONE_SIXTH) {
    return minRgb + (chroma - minRgb) * 6 * hue;
  }
  if (hue < HALF) {
    return chroma;
  }
  if (hue < TWO_THIRDS) {
    return minRgb + (chroma - minRgb) * (TWO_THIRDS - hue) * 6;
  }
  return minRgb;
};

export const hslFromRgb = (red: number, green: number, blue: number): ColorTuple => {
  red /= 255;
  green /= 255;
  blue /= 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);

  let hue = 0;
  let saturation = 50;
  let lightness = (max + min) / 2;

  if (max === min) {
    hue = 0;
    saturation = 0;
  }

  const delta = max - min;
  if (delta === 0) {
    return [0, 0, red * 100];
  }

  if (lightness > 0.5) {
    saturation = delta / (2 - max - min);
  } else {
    saturation = delta / (max + min);
  }

  if (max === red) {
    hue = (green - blue) / delta + (green < blue ? 6 : 0);
  } else if (max === green) {
    hue = (blue - red) / delta + 2;
  } else {
    hue = (red - green) / delta + 4;
  }
  hue /= 6;

  hue *= 360;
  saturation *= 100;
  lightness *= 100;

  return [hue, saturation, lightness];
};

export const rgbFromHex = (hex: string): ColorTuple => {
  const colorValue = parseInt(hex, 16);
  const red = (colorValue & 0xff0000) >> 16;
  const green = (colorValue & 0x00ff00) >> 8;
  const blue = (colorValue & 0x0000ff) >> 0;
  return [red, green, blue];
};

export const rgbFromHsl = (hue: number, saturation: number, lightness: number): ColorTuple => {
  hue *= 0.002777778; // 1 / 360 = 0.002777778; avoiding division
  lightness *= 0.01; // 1 / 100 = 0.01; avoiding division
  saturation *= 0.01;

  const chroma =
    lightness < 0.5
      ? lightness * (1 + saturation)
      : lightness + saturation - lightness * saturation;
  const minRgb = 2 * lightness - chroma;

  const red = hue2rgb(minRgb, chroma, hue + ONE_THIRD) * 255;
  const green = hue2rgb(minRgb, chroma, hue) * 255;
  const blue = hue2rgb(minRgb, chroma, hue - ONE_THIRD) * 255;

  return [red, green, blue];
};

export const hexFromHsl = (hue: number, saturation: number, lightness: number): string =>
  hexValueFromRgb(...rgbFromHsl(hue, saturation, lightness));

export const rgbFromCssColor = (() => {
  const canvas = document.createElement('canvas');
  canvas.height = 1;
  canvas.width = 1;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const rRgb = /^rgb\((\d{1,3}(?:\.\d+)?%?),\s*(\d{1,3}(?:\.\d+)?%?),\s*(\d{1,3}(?:\.\d+)?%?)\)$/;
  const rHsl = /^hsl\((\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)%,\s*(\d+(?:\.\d+)?)%\)$/;

  let lastDataInt: number | null = null;

  return (cssColor: string): ColorTuple => {
    // While it might seem like the default case that gets the color data from a
    // canvas should handle everything here, the problem is it always returns
    // rgba _integers_ instead of _decimals_. When dragging, we need the values
    // to be more precise than that to avoid the swatches drifting around.
    //
    // Internally, we use hsl(...) and maybe rgb(..), and those both allow
    // precise decimals, which is why we support getting those exact values via
    // RegExp here.

    const hslMatch = rHsl.exec(cssColor);
    if (hslMatch) {
      const [_, ...hslString] = hslMatch;
      return rgbFromHsl(...mapStringToNumberTuple(hslString, (str) => Number(str)));
    }

    const rgbMatch = rRgb.exec(cssColor);
    if (rgbMatch) {
      const [_, ...rgbString] = rgbMatch;

      return mapStringToNumberTuple(rgbString, (str) => Number(str));
    }

    if (isNil(ctx)) {
      // eslint-disable-next-line no-console
      console.error('Failed to get 2D context: Your browser may not support Canvas 2D rendering.');
      throw new Error('Canvas 2D context is not available.');
    }

    // This will support any valid CSS color by default.
    ctx.fillStyle = cssColor;
    ctx.fillRect(0, 0, 1, 1);
    const {
      data: [red, green, blue],
    } = ctx.getImageData(0, 0, 1, 1);

    // If the data we got didn't change, then there's a chance this is an invalid
    // color. If so, let's do a more thorough test of that and maybe notify about
    // it.
    const dataInt = (red << 16) | (green << 8) | blue;
    if (dataInt === lastDataInt) {
      const { style } = new Option();
      style.color = cssColor;
      if (style.color === '') {
        // eslint-disable-next-line no-console
        console.warn(
          `Invalid CSS Color "${cssColor}" given. Returning last valid color rgb(${red}, ${green}, ${blue}).`,
        );
      }
    }
    lastDataInt = dataInt;

    return [red, green, blue];
  };
})();

export const hslFromCssColor = (cssColor: string): ColorTuple =>
  hslFromRgb(...rgbFromCssColor(cssColor));

export const rgbFromHsv = (hue: number, saturation: number, value: number): ColorTuple => {
  saturation /= 100;
  value /= 100;
  hue /= 360;
  value = Math.round(value * 255);

  const sector = Math.floor(hue * 6);
  const fractionalSector = hue * 6 - sector;
  const maybeColor1 = Math.round(value * (1 - saturation));
  const maybeColor2 = Math.round(value * (1 - fractionalSector * saturation));
  const maybeColor3 = Math.round(value * (1 - (1 - fractionalSector) * saturation));

  switch (sector % 6) {
    case 0:
      return [value, maybeColor3, maybeColor1];
    case 1:
      return [maybeColor2, value, maybeColor1];
    case 2:
      return [maybeColor1, value, maybeColor3];
    case 3:
      return [maybeColor1, maybeColor2, value];
    case 4:
      return [maybeColor3, maybeColor1, value];
    case 5:
      return [value, maybeColor1, maybeColor2];
    default:
      return [0, 0, 0];
  }
};

export const hsvFromRgb = (red: number, green: number, blue: number): ColorTuple => {
  let computedH = 0;
  let computedS = 0;
  let computedV = 0;

  red /= 255;
  green /= 255;
  blue /= 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);

  // Black-gray-white
  if (min === max) {
    computedV = min;
    return [0, 0, computedV * 100];
  }

  // Colors other than black-gray-white:
  let delta = 0;
  let hue = 0;

  if (red === min) {
    delta = green - blue;
  } else if (blue === min) {
    delta = red - green;
  } else {
    delta = blue - red;
  }

  if (red === min) {
    hue = 3;
  } else if (blue === min) {
    hue = 1;
  } else {
    hue = 5;
  }

  computedH = 60 * (hue - delta / (max - min));
  computedS = (max - min) / max;
  computedV = max;
  return [computedH, computedS * 100, computedV * 100];
};

export const hsvFromCssColor = (cssColor: string): ColorTuple =>
  hsvFromRgb(...rgbFromCssColor(cssColor));
